import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Quotes, CaretLeft, CaretRight, Star } from "@phosphor-icons/react";

const sampleTestimonials = [
  {
    id: "s1",
    author_name: "Aanya R.",
    author_role: "Therapy client",
    division: "couch",
    rating: 5,
    content: "Working with my therapist at MindDhara unlocked a clarity I haven't felt in years. The intake process was thoughtful, I felt seen before the very first session.",
  },
  {
    id: "s2",
    author_name: "Karan M.",
    author_role: "Career mentee",
    division: "forward",
    rating: 5,
    content: "Forward gave me a roadmap I couldn't see for myself. Three months in and I'm interviewing at places I'd been quietly dreaming about for a year.",
  },
  {
    id: "s3",
    author_name: "Dr. Rohit S.",
    author_role: "Corporate Wellbeing Lead",
    division: "couch",
    rating: 5,
    content: "Our team's wellbeing audit and on-going workshops with MindDhara have transformed how we hold space for one another. Genuinely cultural shift.",
  },
];

const CLAMP_LIMIT = 250;

const TestimonialCard = ({ t, i, onOpenChange }) => {
  const [open, setOpen] = useState(false);
  const content = t.content || "";
  const needsClamp = content.length > CLAMP_LIMIT;
  const shown = !needsClamp || open ? content : `${content.slice(0, CLAMP_LIMIT).trimEnd()}… `;

  const toggle = () => {
    setOpen((o) => {
      onOpenChange?.(i, !o);
      return !o;
    });
  };

  return (
    <article className="testimonial-card" data-testid={`testimonial-${i}`}>
      <Quotes size={48} weight="fill" className="testimonial-quote-mark" />
      <p className="testimonial-content">
        {shown}
        {needsClamp && (
          <button type="button" onClick={toggle} className="testimonial-readmore-inline" data-testid={`testimonial-readmore-${i}`}>
            {open ? "Show Less" : "Read More"}
          </button>
        )}
      </p>
      <div className="testimonial-rating">
        {Array.from({ length: t.rating || 5 }).map((_, k) => (
          <Star key={`star-${k}`} size={16} weight="fill" />
        ))}
      </div>
      <div className="testimonial-author">
        <div className="testimonial-author-avatar">{(t.author_name || "M")[0]}</div>
        <div>
          <div className="testimonial-author-name">{t.author_name}</div>
          <div className="testimonial-author-role">{t.author_role || "MindDhara client"}</div>
        </div>
      </div>
    </article>
  );
};

export default function TestimonialCarousel() {
  const [items, setItems] = useState(sampleTestimonials);
  const [idx, setIdx] = useState(0);
  const [googleUrl, setGoogleUrl] = useState("");
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/testimonials/public", { params: { limit: 12 } })
      .then((r) => { if (r.data?.length) setItems(r.data); })
      .catch(() => {});
    api.get("/settings/public")
      .then((r) => setGoogleUrl(r.data?.google_business_url || ""))
      .catch(() => {});
  }, []);

  const next = useCallback(() => setIdx((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + items.length) % items.length), [items.length]);

  // Swipe support (arrows are hidden on mobile)
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) prev();
    else if (dx < -40) next();
    touchX.current = null;
  };

  // Pause auto-advance while hovered or while any testimonial is expanded
  const [hovered, setHovered] = useState(false);
  const [expandedMap, setExpandedMap] = useState({});
  const anyExpanded = Object.values(expandedMap).some(Boolean);
  const onOpenChange = useCallback((i, open) => {
    setExpandedMap((m) => ({ ...m, [i]: open }));
  }, []);

  // Auto-advance every 6s (paused on hover / while reading an expanded story)
  useEffect(() => {
    if (hovered || anyExpanded) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, hovered, anyExpanded]);

  return (
    <section className="md-section testimonials-section" data-testid="testimonials-section">
      <div className="md-container">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="md-label-eyebrow mb-3">Stories from our community</div>
          <h2 className="text-3xl md:text-5xl font-serif mb-3">What people say after working with us</h2>
          <p className="text-base" style={{ color: "var(--md-text-soft)" }}>
            Real voices from clients, mentees, and organisations we&apos;ve held space for.
          </p>
        </div>

        <div className="testimonials-stage" data-testid="testimonials-stage" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
          <button onClick={prev} className="testimonials-nav prev" aria-label="Previous testimonial" data-testid="testimonials-prev">
            <CaretLeft size={18} weight="bold" />
          </button>

          <div className="testimonials-viewport" ref={trackRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div className="testimonials-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
              {items.map((t, i) => <TestimonialCard key={t.id || i} t={t} i={i} onOpenChange={onOpenChange} />)}
            </div>
          </div>

          <button onClick={next} className="testimonials-nav next" aria-label="Next testimonial" data-testid="testimonials-next">
            <CaretRight size={18} weight="bold" />
          </button>
        </div>

        <div className="testimonials-dots" role="tablist" aria-label="Testimonial pagination" data-testid="testimonials-dots">
          {items.map((t, i) => (
            <button key={t.id || `dot-${i}`} onClick={() => setIdx(i)} className={`testimonials-dot ${idx === i ? "active" : ""}`} aria-label={`Go to testimonial ${i + 1}`} data-testid={`testimonials-dot-${i}`} />
          ))}
        </div>

        <div className="text-center mt-10 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/share-your-story" className="btn-outline text-sm" data-testid="testimonials-share-cta">
            Share Your Story <ArrowRightInline />
          </Link>
          {googleUrl && (
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm" data-testid="testimonials-google-review-cta">
              Review Us on Google <ArrowRightInline />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

const ArrowRightInline = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
